import React, { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import {
  Typography,
  TextField,
  Button,
  IconButton,
  Tabs,
  Tab,
  Paper,
  Pagination,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import API_BASE_URL from "../../../../Constants";
import { HeadersContext } from "./headersContext";

/** Same status + fieldsMap from your code. */
const complianceStatusMap = {
  "Pre-Filing": "Pre-Filing",
  "Pay to File": "Pay to File",
  "In Process": "In Process",
  "Processed": "Processed",
};

const fieldsMap = {
  "Pre-Filing": [
    "Group Number",
    "Tracking Number",
    "Device ID",
    "Device Type",
    "BIN",
    "Address",
    "Borough",
    "Compliance Report Status",
    "Inspection Type",
    "Inspection/Test Date",
    "Filing Due",
    "Related Cycle End Date",
    "Last Day To File",
    "Modified Date",
    "Created Date",
  ],
  "Pay to File": [
    "Group Number",
    "Tracking Number",
    "Device ID",
    "Device Type",
    "BIN",
    "Address",
    "Borough",
    "Compliance Report Status",
    "Inspection Type",
    "Filing Fee",
    "Late Filing Penalty",
    "Amount Due",
    "Modified Date",
    "Created Date",
  ],
  "In Process": [
    "Group Number",
    "Tracking Number",
    "Device ID",
    "Device Type",
    "BIN",
    "Address",
    "Borough",
    "Compliance Report Status",
    "Inspection Type",
    "Perf-Insp Attest",
    "Perf-Dir Attest",
    "Owner Attest",
    "Wit-Dir Attest",
    "Wit-Insp Attest",
    "Modified Date",
    "Created Date",
  ],
  "Processed": [
    "Group Number",
    "Tracking Number",
    "Device ID",
    "Device Type",
    "BIN",
    "Address",
    "Borough",
    "Compliance Report Status",
    "Inspection Type",
    "Filed Date",
    "Payment Status",
    "Amount Paid",
    "Payment Method",
    "Payment Transaction Date",
    "Modified Date",
    "Created Date",
  ],
};

const statusIdMap = {
  "Pre-Filing": [1],
  "Pay to File": [7],
  "In Process": [2, 4, 11, 6, 8],
  "Processed": [5, 9, 10, 12],
};

export default function DeviceList() {
  const [devices, setDevices] = useState([]);
  const [totalRows, setTotalRows] = useState(0);

  /** We do "page" as 1-based, to match the second snippet’s style. */
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [searchQuery, setSearchQuery] = useState("");
  const [selectedStatus, setSelectedStatus] = useState("Pre-Filing");
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const { headers } = useContext(HeadersContext);

  useEffect(() => {
    fetchDevices(page, rowsPerPage, searchQuery, selectedStatus);
    // eslint-disable-next-line
  }, [page, rowsPerPage, searchQuery, selectedStatus]);

  async function fetchDevices(currentPage, size, query, statusLabel) {
    try {
      setIsLoading(true);
      const statusIds = statusIdMap[statusLabel].join(",");
      const zeroBasedPage = currentPage - 1; // must convert 1-based to 0-based
      const url = `${API_BASE_URL}/api/admin/getDobDevicesPage?page=${zeroBasedPage}&size=${size}&statusIds=${statusIds}&query=${encodeURIComponent(
        query.trim().toLowerCase()
      )}`;

      const response = await fetch(url, {
        method: "GET",
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      setTotalRows(data.totalElements || 0);

      // sort by createdDate desc
      const sortedDevices = (data.content || []).sort(
        (a, b) => new Date(b.createdDate) - new Date(a.createdDate)
      );
      setDevices(sortedDevices);
    } catch (err) {
      console.error("Fetch error: ", err);
      setDevices([]);
      setTotalRows(0);
    } finally {
      setIsLoading(false);
    }
  }

  const handleStatusChange = (e, newValue) => {
    setSelectedStatus(newValue);
    setPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearch = () => {
    setPage(1);
    fetchDevices(1, rowsPerPage, searchQuery, selectedStatus);
  };

  /** Device or group clicks */
  const handleDeviceClick = (deviceID) => {
    navigate(`/inspections-violations/device-defects/${deviceID}`, {
      state: { headers },
    });
  };

  const handleGroupClick = (groupNumber) => {
    navigate(`/inspections-violations/group-number-devices/${groupNumber}`);
  };

  const handleCreateElv3Click = () => {
    navigate("/inspections-violations/create-elv3", { state: { headers } });
  };

  /** Convert totalRows + rowsPerPage => totalPages. We do ceiling. */
  const totalPages = Math.ceil(totalRows / rowsPerPage) || 1;

  function handlePageChange(event, newPageVal) {
    setPage(newPageVal);
  }

  function handleRowsPerPageChange(e) {
    setRowsPerPage(Number(e.target.value));
    setPage(1);
  }

  /** 
   * Convert a field to the actual device property name (like your original code).
   */
  function getDeviceFieldKey(field) {
    const map = {
      "Tracking Number": "trackingNumber",
      "Device Type": "deviceType",
      BIN: "bin",
      Address: "location",
      Borough: "borough",
      "Compliance Report Status": "complianceReportStatus",
      "Inspection Type": "inspectionType",
      "Inspection/Test Date": "inspectionDate",
      "Filing Due": "filingDue",
      "Related Cycle End Date": "relatedCycleEndDate",
      "Last Day To File": "lastdayToFile",
      "Modified Date": "modifiedDate",
      "Created Date": "createdDate",
      "Filing Fee": "filingFee",
      "Late Filing Penalty": "lateFilingPenalty",
      "Amount Due": "amountDue",
      "Perf-Insp Attest": "performingAgencyInspectorStatement",
      "Perf-Dir Attest": "performingAgencyDirectorStatement",
      "Owner Attest": "ownerLegalStatement",
      "Wit-Dir Attest": "witnessingAgencyDirectorStatement",
      "Wit-Insp Attest": "witnessingAgencyInspStatement",
      "Filed Date": "filedDate",
      "Payment Status": "paymentStatus",
      "Amount Paid": "amountPaid",
      "Payment Method": "paymentMethod",
      "Payment Transaction Date": "paymentTransactionDate",
    };
    return map[field] || field.toLowerCase().replace(/\s+/g, "");
  }

  /** Renders a single cell's content. */
  function renderCell(device, field) {
    // special "Group Number"
    if (field === "Group Number") {
      return (
        <span
          onClick={() => handleGroupClick(device.groupNumber)}
          className="text-blue-600 hover:underline cursor-pointer"
        >
          {device.groupNumber}
        </span>
      );
    }
    // special "Device ID"
    if (field === "Device ID") {
      return (
        <span
          onClick={() => handleDeviceClick(device.deviceID)}
          className="text-blue-600 hover:underline cursor-pointer"
        >
          {device.deviceID}
        </span>
      );
    }

    // else map field => device property
    const key = getDeviceFieldKey(field);

    // if it's boolean (like Perf-Insp Attest)
    if (
      key === "performingAgencyInspectorStatement" ||
      key === "performingAgencyDirectorStatement" ||
      key === "ownerLegalStatement" ||
      key === "witnessingAgencyDirectorStatement" ||
      key === "witnessingAgencyInspStatement"
    ) {
      return device[key] === true ? "Yes" : "No";
    }
    return device[key] || "";
  }

  // Filter based on search & status
  const displayedDevices = devices.filter((device) => {
    const matchesSearch = device.deviceID
      ?.toLowerCase()
      .includes(searchQuery.toLowerCase());
    const matchesStatus = statusIdMap[selectedStatus].includes(
      parseInt(device.complianceReportStatusId, 10)
    );
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="flex flex-col h-screen">
      {/* TOP SECTION: "DOB Device List" + search + "Create ELV3" button */}
      <div className="p-2 bg-gray-100 border-b flex flex-wrap items-center justify-between">
    
        <div className="flex items-center space-x-2 sm:mt-0">
          <Button
            variant="contained"
            color="primary"
            onClick={handleCreateElv3Click}
          >
            Create ELV3
          </Button>
          <TextField
            size="small"
            variant="outlined"
            placeholder="Search by Device ID..."
            value={searchQuery}
            onChange={handleSearchChange}
          />
          <IconButton onClick={handleSearch} color="primary">
            <SearchIcon />
          </IconButton>
        </div>
      </div>

      {/* Status Tabs */}
      <Tabs
        value={selectedStatus}
        onChange={handleStatusChange}
        indicatorColor="primary"
        textColor="primary"
        variant="fullWidth"
        className="shadow-md bg-white"
      >
        {Object.keys(complianceStatusMap).map((label) => (
          <Tab key={label} label={label} value={label} className="font-semibold" />
        ))}
      </Tabs>

      {/* TABLE SECTION */}
      <div className="flex-1 overflow-auto p-2">
        {/* If loading, you can show a spinner or something */}
        {isLoading && (
          <div className="text-center mt-4 text-gray-500">Loading...</div>
        )}
        {!isLoading && (
          <Paper className="overflow-auto shadow-md rounded-md">
            <table className="min-w-full bg-white border border-gray-200">
              <thead className="bg-gray-100">
                <tr>
                  {fieldsMap[selectedStatus].map((header) => (
                    <th key={header} className="px-4 py-2 text-left font-bold text-gray-700">
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="text-gray-800">
                {displayedDevices.map((device, index) => {
                  const isEven = index % 2 === 0;
                  return (
                    <tr
                      key={device.trackingNumber}
                      className={`border-b hover:bg-gray-50 transition ${
                        isEven ? "bg-gray-50" : "bg-white"
                      }`}
                    >
                      {fieldsMap[selectedStatus].map((field) => (
                        <td key={field} className="px-4 py-2 whitespace-nowrap">
                          {renderCell(device, field)}
                        </td>
                      ))}
                    </tr>
                  );
                })}
                {displayedDevices.length === 0 && (
                  <tr>
                    <td
                      colSpan={fieldsMap[selectedStatus].length}
                      className="text-center py-4 text-gray-500"
                    >
                      No devices found.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </Paper>
        )}
      </div>

      {/* BOTTOM PAGINATION BAR, FIXED */}
      <div className="bg-white border-t border-gray-300 p-3 flex items-center justify-between">
        {/* MUI <Pagination> for page numbers */}
        <Pagination
          count={totalPages}
          page={page}
          onChange={handlePageChange}
          color="primary"
          shape="rounded"
        />

        {/* Rows per page select */}
        <div className="flex items-center space-x-2">
          <span>Rows per page:</span>
          <select
            value={rowsPerPage}
            onChange={handleRowsPerPageChange}
            className="border border-gray-300 rounded px-2 py-1"
          >
            {[25, 50, 100, 200].map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>
      </div>
    </div>
  );
}
